import { Component, EventEmitter, Input, OnInit, Output, Injector } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';

import { isValidEmail } from '../helpers';
import { Circle, User } from 'app/models';
import { UsersRepository } from 'app/repositories';
import { CirclesRepository } from 'app/repositories/circles.repository';
import { SpheresRepository } from 'app/repositories/spheres.repository';
import { SettingsRepository } from 'app/repositories/settings.repository';

@Component({
  selector: 'flxion-user-select',
  templateUrl: './user-select.component.html',
  styleUrls: ['./user-select.component.scss']
})
export class UserSelectComponent implements OnInit {

  @Input() mode: 'add'|'select' = 'add';
  @Input() tooltip: string = null;
  @Input() initialValue: string = '';
  @Input() label: string;
  @Input() skipUserIds: number[] = [];
  @Output() onSelect = new EventEmitter<User>();

  // Explicit context circle. If provided, use its users and its sphere's users.
  @Input() circle: Circle | null = null;
  // Force using known users only, regardless of context.
  @Input() useKnownUsersOnly: boolean = false;

  // knownUsers is now only used as a fallback if the dynamic sources (circle/sphere users) are empty
  knownUsers: User[] = [];

  userFilter = new FormControl();
  filteredUsers: Observable<User[]>;

  constructor(
    private circleRepo: CirclesRepository,
    private sphereRepo: SpheresRepository,
    private settingsRepo: SettingsRepository,
    private injector: Injector,
  ) {}

  ngOnInit() {
    this.userFilter.setValue(this.initialValue);
    this.initFilter();
  }

  onBlur() {
    this.userFilter.setErrors(this.userFilter.value && !(this.userFilter.value instanceof User) ? {e: 1} : null);
    if (!this.userFilter.value && this.mode === 'select') {
      this.onSelect.emit(null);
    }
  }

  private initFilter() {
    const value$ = this.userFilter.valueChanges.pipe(
        startWith(this.userFilter.value as string || '')
    );

    this.filteredUsers = combineLatest([
      value$,
      this.circleRepo.activeCircle$,
      this.sphereRepo.activeSphere$,
      this.settingsRepo.contextActive$,
    ]).pipe(
      map(
          ([value, activeCircle, activeSphere, contextActive]) => {
            // Determine effective users based on inputs and settings
            let circleUsers: User[] | null;
            let sphereUsers: User[] | null;

            if (this.useKnownUsersOnly) {
              // Force known users only
              circleUsers = null;
              sphereUsers = null;
            } else if (this.circle) {
              // Explicit circle passed in
              circleUsers = this.circle.users || [];
              sphereUsers = this.circle.sphere?.users || [];
            } else if (contextActive) {
              // Use active context only when contextActive is on
              circleUsers = activeCircle?.users || null;
              sphereUsers = activeSphere?.users || null;
            } else {
              // Context inactive: fall back to known users
              circleUsers = null;
              sphereUsers = null;
            }

            return this.filterUsers(value, circleUsers, sphereUsers);
          }
      )
    );
  }

  // Filter suggestions using active circle/sphere; fall back to all users when both are empty.
  private filterUsers(value: string, circleUsers: User[]|null, sphereUsers: User[]|null): User[] {
    if (typeof value !== 'string') {
      // On selecting an option, value is a User and will be immediately cleared, so just return.
      return;
    }

    // Only show those that aren't already in this meeting's list.
    if (value === '') {
      return [];
    }

    // Partial match of name, or the beginning of an email address if user has no name.
    const lc = value.trim().toLowerCase();

    const matches = (u: User) => !this.skipUserIds.includes(u.id)
      && (u.name?.toLowerCase().includes(lc) || (!u.name && u.email?.toLowerCase().startsWith(lc)));

    const hasActiveContext = (circleUsers?.length || 0) > 0 || (sphereUsers?.length || 0) > 0;

    if (!hasActiveContext) {
      // Fallback to global known users, but only load them lazily once to avoid instantiating the repo when not needed.
      if (!this.knownUsers || this.knownUsers.length === 0) {
        try {
          const usersRepo = this.injector.get(UsersRepository);
          usersRepo.users$.pipe(take(1)).subscribe(data => this.knownUsers = data || []);
          // It may take a second before this population happens; that's fine, the filter will work next time then.
        } catch (e) {
          // In case DI fails, keep knownUsers empty; we will still allow email entry.
        }
      }
      const filtered = (this.knownUsers || []).filter(matches);
      if (filtered.length) {
        return filtered;
      }
      if (lc && isValidEmail(lc)) {
        // Create a 'fake' user with just that email, and return it as a selectable match.
        return [new User({ email: lc })];
      }
      return [];
    }

    const cu = (circleUsers || []).filter(matches);
    // Remove circle users from sphere users (by id)
    const circleIds = new Set((circleUsers || []).map(u => u.id));
    const su = (sphereUsers || []).filter(u => !circleIds.has(u.id)).filter(matches);

    const combined = [...cu, ...su];
    if (combined.length) {
      return combined;
    }
    if (lc && isValidEmail(lc)) {
      return [new User({ email: lc })];
    }
    return [];
  }

  // A filtered value was selected.
  optionSelected(event) {
    // Send it to the parent.
    this.onSelect.emit(event.option.value as User);
    if (this.mode === 'add') {
      // Reset filter.
      this.userFilter.setValue('');
    }
  }

  displayWith(value) {
    return value instanceof User ? value.name : value;
  }

  // Allow drop by preventing default dragover behavior.
  onDragOver(event: DragEvent) {
    if (event) {
      event.preventDefault();
    }
  }

  // Handle dropped text; sanitize by attempting to extract an email address.
  onDrop(event: DragEvent) {
    if (!event) {
      return;
    }
    event.preventDefault();

    const dt = event.dataTransfer;
    let text = '';
    if (dt) {
      // Prefer text/plain; fall back to generic 'text'.
      text = dt.getData('text/plain') || dt.getData('text') || '';
    }

    if (typeof text === 'string') {
      // First, if the string contains '<...>' extract the part between angle brackets.
      let extracted = text;
      const angleMatch = extracted.match(/<\s*([^>]+)\s*>$/);
      if (angleMatch) {
        extracted = angleMatch[1].trim();
      }

      // Then remove any leading whitespace and a case-insensitive 'mailto:' prefix.
      const sanitized = extracted.replace(/^\s*mailto:/i, '').trim();
      this.userFilter.setValue(sanitized);
    }
  }

}
